import { useState, useEffect, useCallback } from 'react';
import { apiFetch } from '../utils/streamApi';
import ConfirmDialog from '../components/ConfirmDialog';
// Shared with the Ops Console alert panel — same bar, same threshold colours,
// so a score means the same thing wherever an operator reads it.
import { ScoreBar, CandidateList } from '../components/FaceMatchScore';
import '../css/parvekshak.css';

const PER_PAGE = 30;

function SuppressMeta({ entry }) {
  const pathMap = {
    strong_confirm:   { text: 'Strong',  bg: 'rgba(34,197,94,0.15)',   color: '#22c55e' },
    leave_frame_soft: { text: 'Soft',    bg: 'rgba(245,158,11,0.15)',  color: '#f59e0b' },
  };
  const sceneMap = {
    DAY:        { text: '☀ Day',        color: '#60a5fa' },
    LIT_NIGHT:  { text: '🌆 Night',     color: '#a78bfa' },
    DARK_NIGHT: { text: '🌙 Dark',      color: '#94a3b8' },
  };
  const p = pathMap[entry.suppress_path];
  const s = sceneMap[entry.scene];
  if (!p && !s) return null;
  return (
    <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', alignItems: 'center' }}>
      {p && (
        <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 6px', borderRadius: 4,
          background: p.bg, color: p.color }}>{p.text}</span>
      )}
      {s && (
        <span style={{ fontSize: 10, fontWeight: 600, color: s.color }}>{s.text}</span>
      )}
      {entry.frames_above != null && (
        <span style={{ fontSize: 10, color: 'var(--text-muted)' }}>{entry.frames_above}f</span>
      )}
      {entry.margin != null && (
        <span style={{ fontSize: 10, color: 'var(--text-muted)' }}>Δ{Math.round(entry.margin * 100)}%</span>
      )}
      {entry.threshold_used != null && (
        <span style={{ fontSize: 10, color: 'var(--text-muted)' }}>thr {Math.round(entry.threshold_used * 100)}%</span>
      )}
    </div>
  );
}

function VerdictBadge({ verdict }) {
  if (!verdict) return null;
  const map = {
    correct_suppression: { text: '✓ Correct',   bg: 'rgba(34,197,94,0.15)',  color: '#22c55e' },
    wrong_suppression:   { text: '✗ Wrong',      bg: 'rgba(239,68,68,0.15)',  color: '#ef4444' },
  };
  const s = map[verdict] || { text: verdict, bg: 'rgba(255,255,255,0.08)', color: 'var(--text-muted)' };
  return (
    <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 6px', borderRadius: 4,
      background: s.bg, color: s.color, letterSpacing: '0.03em' }}>
      {s.text}
    </span>
  );
}

function SnapImg({ entryId, onOpen }) {
  const [src, setSrc] = useState(null);
  useEffect(() => {
    let url;
    apiFetch(`/known-seen/${encodeURIComponent(entryId)}/snapshot`)
      .then(r => r.ok ? r.blob() : null)
      .then(blob => { if (blob) { url = URL.createObjectURL(blob); setSrc(url); } })
      .catch(() => {});
    return () => { if (url) URL.revokeObjectURL(url); };
  }, [entryId]);
  if (!src) return (
    <div style={{ width: '100%', aspectRatio: '4/3', background: 'var(--surface-alt)',
      borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center',
      color: 'var(--text-muted)', fontSize: 28 }}>👤</div>
  );
  return (
    <img src={src} alt="snapshot"
      onClick={() => onOpen && onOpen(src)}
      style={{ width: '100%', borderRadius: 8, display: 'block', objectFit: 'contain',
        background: '#000', aspectRatio: '16/9', cursor: onOpen ? 'zoom-in' : 'default' }} />
  );
}

function Lightbox({ src, onClose }) {
  useEffect(() => {
    const onKey = e => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);
  if (!src) return null;
  return (
    <div onClick={onClose}
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.88)', zIndex: 1000,
        display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'zoom-out' }}>
      <img src={src} alt="snapshot enlarged"
        onClick={e => e.stopPropagation()}
        style={{ maxWidth: '92vw', maxHeight: '88vh', objectFit: 'contain', borderRadius: 8,
          boxShadow: '0 8px 40px rgba(0,0,0,0.7)' }} />
      <button onClick={onClose}
        style={{ position: 'absolute', top: 16, right: 20, background: 'none', border: 'none',
          color: '#fff', fontSize: 28, cursor: 'pointer', lineHeight: 1 }}>✕</button>
    </div>
  );
}

function ConfirmedFramesModal({ view, onClose, onOpen }) {
  const [srcs, setSrcs] = useState([]);
  useEffect(() => {
    if (!view) { setSrcs([]); return; }
    let urls = [];
    let cancelled = false;
    (async () => {
      const loaded = [];
      for (let i = 0; i < view.count; i++) {
        try {
          const r = await apiFetch(`/known-seen/${encodeURIComponent(view.id)}/frame/${i}`);
          if (r.ok) { const u = URL.createObjectURL(await r.blob()); urls.push(u); loaded.push(u); }
          else loaded.push(null);
        } catch { loaded.push(null); }
      }
      if (!cancelled) setSrcs(loaded);
      else urls.forEach(u => URL.revokeObjectURL(u));
    })();
    return () => { cancelled = true; urls.forEach(u => URL.revokeObjectURL(u)); };
  }, [view]);
  useEffect(() => {
    const onKey = e => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);
  if (!view) return null;
  const fmtTs = t => (t ? new Date(t * 1000).toLocaleString() : '—');
  return (
    <div onClick={onClose}
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.9)', zIndex: 1100,
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
      <div onClick={e => e.stopPropagation()}
        style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 12,
          padding: 16, maxWidth: '92vw', maxHeight: '88vh', overflow: 'auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, gap: 16 }}>
          <h3 style={{ margin: 0, fontSize: 14 }}>🔍 Confirmed frames — what scored above threshold</h3>
          <button onClick={onClose}
            style={{ background: 'none', border: 'none', color: 'var(--text)', fontSize: 22, cursor: 'pointer', lineHeight: 1 }}>✕</button>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 12 }}>
          {Array.from({ length: view.count }).map((_, i) => (
            <div key={i}>
              {srcs[i]
                ? <img src={srcs[i]} alt={`confirmed frame ${i + 1}`} onClick={() => onOpen && onOpen(srcs[i])}
                    style={{ width: '100%', borderRadius: 8, background: '#000', cursor: 'zoom-in',
                      objectFit: 'contain', aspectRatio: '16/9', display: 'block' }} />
                : <div style={{ width: '100%', aspectRatio: '16/9', background: 'var(--surface-alt)', borderRadius: 8,
                    display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-muted)' }}>…</div>}
              <div style={{ fontSize: 11, color: 'var(--text-muted)', textAlign: 'center', marginTop: 4 }}>
                Frame {i + 1} · {fmtTs(view.ts?.[i])}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function Toast({ msg, type }) {
  if (!msg) return null;
  return <div className={`pk-toast ${type}`}>{msg}</div>;
}

export default function KnownSeenPage() {
  const [entries,     setEntries]    = useState([]);
  const [loading,     setLoading]    = useState(true);
  const [filter,      setFilter]     = useState('all');   // all | unlabelled | correct | wrong
  const [nameFilter,  setNameFilter] = useState('');
  const [toast,       setToast]      = useState(null);
  const [confirm,     setConfirm]    = useState(null);
  const [savingId,    setSavingId]   = useState(null);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [downloading, setDownloading] = useState(false);
  const [lightboxSrc, setLightboxSrc] = useState(null);
  const [framesView,  setFramesView]  = useState(null);

  const showToast = (msg, type = 'ok') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 2800);
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await apiFetch('/known-seen?n=200');
      const d = await r.json();
      setEntries(d.entries || []);
    } catch {
      showToast('Failed to load', 'err');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const setVerdict = async (entryId, verdict) => {
    setSavingId(entryId);
    try {
      const r = await apiFetch(`/known-seen/${encodeURIComponent(entryId)}/verdict`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ verdict }),
      });
      if (r.ok) {
        setEntries(prev => prev.map(e =>
          e.entry_id === entryId ? { ...e, verdict } : e
        ));
        showToast(verdict === 'correct_suppression' ? '✓ Marked correct' : '✗ Marked wrong');
      } else {
        showToast('Save failed', 'err');
      }
    } catch {
      showToast('Error', 'err');
    } finally {
      setSavingId(null);
    }
  };

  const toggleSelect = (id) => setSelectedIds(prev => {
    const next = new Set(prev);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  });

  const downloadJson = async () => {
    setDownloading(true);
    try {
      const body = selectedIds.size > 0
        ? { entry_ids: [...selectedIds] }
        : {
            verdict: filter === 'correct' ? 'correct_suppression'
                   : filter === 'wrong'   ? 'wrong_suppression'
                   : undefined,
            name: nameFilter || undefined,
          };
      const r = await apiFetch('/known-seen/download', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!r.ok) { showToast('Download failed', 'err'); return; }
      const blob = await r.blob();
      const cd   = r.headers.get('Content-Disposition') || '';
      const match = cd.match(/filename="([^"]+)"/);
      const fname = match ? match[1] : 'known_seen.json';
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = fname;
      a.click();
      URL.revokeObjectURL(a.href);
    } catch {
      showToast('Download error', 'err');
    } finally {
      setDownloading(false);
    }
  };

  const clearAll = () => setConfirm({
    title: 'Clear all Known Seen entries?',
    message: 'All entries and snapshots will be removed. This cannot be undone.',
    onConfirm: async () => {
      setConfirm(null);
      await apiFetch('/known-seen', { method: 'DELETE' });
      setEntries([]);
      showToast('Cleared');
    },
  });

  // All unique person names for filter chips
  const allNames = [...new Set(entries.map(e => e.known_name).filter(Boolean))].sort();

  const visible = entries.filter(e => {
    if (filter === 'unlabelled' && e.verdict != null) return false;
    if (filter === 'correct'    && e.verdict !== 'correct_suppression') return false;
    if (filter === 'wrong'      && e.verdict !== 'wrong_suppression')   return false;
    if (nameFilter && e.known_name !== nameFilter) return false;
    return true;
  });

  const unlabelledCount = entries.filter(e => e.verdict == null).length;
  const correctCount    = entries.filter(e => e.verdict === 'correct_suppression').length;
  const wrongCount      = entries.filter(e => e.verdict === 'wrong_suppression').length;

  return (
    <div className="pk-page">
      {/* Header */}
      <div className="pk-header">
        <div className="pk-header-left">
          <h1>👤 Known Seen</h1>
          <p>Review people the system recognised and didn't alert on — confirm if it got each one right</p>
        </div>
        <div className="pk-header-right">
          {unlabelledCount > 0 && (
            <span className="pk-stat">
              <strong style={{ color: '#f59e0b' }}>{unlabelledCount}</strong> unlabelled
            </span>
          )}
          <span className="pk-stat"><strong>{entries.length}</strong> total</span>
          <button
            onClick={downloadJson}
            disabled={downloading || visible.length === 0}
            style={{ padding: '6px 12px', borderRadius: 8, border: '1px solid var(--border)',
              background: 'var(--surface-alt)', color: 'var(--text)', cursor: 'pointer',
              fontSize: 12, fontWeight: 600, opacity: (downloading || visible.length === 0) ? 0.5 : 1 }}>
            {downloading ? '…' : selectedIds.size > 0
              ? `⬇ Download (${selectedIds.size} selected)`
              : `⬇ Download (${visible.length})`}
          </button>
        </div>
      </div>

      {/* Toolbar */}
      <div className="page-toolbar">
        <button className={`tb-btn${filter === 'all'        ? ' active' : ''}`} onClick={() => setFilter('all')}>All ({entries.length})</button>
        <button className={`tb-btn${filter === 'unlabelled' ? ' active' : ''}`} onClick={() => setFilter('unlabelled')}
          style={unlabelledCount > 0 ? { color: '#f59e0b', borderColor: '#f59e0b44' } : {}}>
          Unlabelled ({unlabelledCount})
        </button>
        <button className={`tb-btn${filter === 'correct'    ? ' active' : ''}`} onClick={() => setFilter('correct')}>✓ Correct ({correctCount})</button>
        <button className={`tb-btn${filter === 'wrong'      ? ' active' : ''}`} onClick={() => setFilter('wrong')}>✗ Wrong ({wrongCount})</button>

        {allNames.length > 1 && (
          <>
            <div className="tb-sep" />
            <select className="tb-input" style={{ borderRadius: 6, paddingLeft: 8, width: 'auto', cursor: 'pointer' }}
              value={nameFilter} onChange={e => setNameFilter(e.target.value)}>
              <option value="">All People</option>
              {allNames.map(n => <option key={n} value={n}>{n}</option>)}
            </select>
          </>
        )}

        <div className="tb-sep" />
        <button className="tb-ghost-btn" onClick={load}>↺ Refresh</button>
        <div className="tb-sep" />
        <button className="tb-ghost-btn" style={{ color: '#e74c3c' }} onClick={clearAll}>🗑 Clear All</button>
      </div>

      {loading ? (
        <div className="pk-loading">⏳ Loading…</div>
      ) : visible.length === 0 ? (
        <div className="pk-empty">
          <div className="pk-empty-icon">👤</div>
          <h3>{entries.length === 0 ? 'No entries yet' : 'No entries match filter'}</h3>
          <p>{entries.length === 0
            ? "Recognised people will appear here so you can confirm whether it got each one right."
            : 'Change the filter to see more entries.'}</p>
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 14, padding: '0 0 24px' }}>
          {visible.map(e => {
            const dt  = new Date(e.ts * 1000).toLocaleString();
            const busy = savingId === e.entry_id;
            return (
              <div key={e.entry_id}
                style={{ background: 'var(--surface)',
                  border: `1px solid ${selectedIds.has(e.entry_id) ? '#3b82f6' : 'var(--border)'}`,
                  borderRadius: 12, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>

                {/* Photo */}
                <div style={{ padding: 10, position: 'relative' }}>
                  <SnapImg entryId={e.entry_id} onOpen={setLightboxSrc} />
                  <input type="checkbox" checked={selectedIds.has(e.entry_id)}
                    onChange={() => toggleSelect(e.entry_id)}
                    style={{ position: 'absolute', top: 16, right: 16, width: 16, height: 16,
                      cursor: 'pointer', accentColor: '#3b82f6' }} />
                </div>

                {/* Info */}
                <div style={{ padding: '0 12px 10px', flex: 1, display: 'flex', flexDirection: 'column', gap: 6 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ fontSize: 14, fontWeight: 700, color: 'var(--text)' }}>{e.known_name}</span>
                    <VerdictBadge verdict={e.verdict} />
                  </div>
                  <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                    📷 {e.cam_name} &nbsp;·&nbsp; {dt}
                  </div>
                  <SuppressMeta entry={e} />

                  {/* Matched score */}
                  <div>
                    <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase',
                      letterSpacing: '0.06em', marginBottom: 3 }}>Match Score</div>
                    <ScoreBar score={e.face_score} />
                  </div>

                  {/* All candidates */}
                  <CandidateList candidates={e.known_candidates} matchedName={e.known_name} />

                  {/* Confirmed frames — the actual frames that scored above threshold */}
                  {e.confirmed_frames > 0 && (
                    <button onClick={() => setFramesView({ id: e.entry_id, count: e.confirmed_frames, ts: e.confirmed_frame_ts })}
                      style={{ marginTop: 2, alignSelf: 'flex-start', padding: '5px 9px', borderRadius: 6,
                        border: '1px solid var(--border)', background: 'var(--surface-alt)', color: 'var(--text)',
                        cursor: 'pointer', fontSize: 11, fontWeight: 600 }}>
                      🔍 {e.confirmed_frames} confirmed frame{e.confirmed_frames > 1 ? 's' : ''}
                    </button>
                  )}
                </div>

                {/* Verdict buttons */}
                <div style={{ display: 'flex', gap: 8, padding: '10px 12px', borderTop: '1px solid var(--border)' }}>
                  <button disabled={busy} onClick={() => setVerdict(e.entry_id, 'correct_suppression')}
                    style={{ flex: 1, padding: '7px 0', borderRadius: 8, border: 'none', cursor: busy ? 'wait' : 'pointer',
                      fontWeight: 600, fontSize: 12,
                      background: e.verdict === 'correct_suppression' ? '#22c55e' : 'rgba(34,197,94,0.12)',
                      color:      e.verdict === 'correct_suppression' ? '#fff'    : '#22c55e' }}>
                    ✓ Correct
                  </button>
                  <button disabled={busy} onClick={() => setVerdict(e.entry_id, 'wrong_suppression')}
                    style={{ flex: 1, padding: '7px 0', borderRadius: 8, border: 'none', cursor: busy ? 'wait' : 'pointer',
                      fontWeight: 600, fontSize: 12,
                      background: e.verdict === 'wrong_suppression' ? '#ef4444' : 'rgba(239,68,68,0.12)',
                      color:      e.verdict === 'wrong_suppression' ? '#fff'    : '#ef4444' }}>
                    ✗ Wrong
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <ConfirmedFramesModal view={framesView} onClose={() => setFramesView(null)} onOpen={setLightboxSrc} />
      <Lightbox src={lightboxSrc} onClose={() => setLightboxSrc(null)} />
      <Toast msg={toast?.msg} type={toast?.type} />

      {confirm && (
        <ConfirmDialog
          title={confirm.title}
          message={confirm.message}
          confirmLabel="Clear"
          confirmClass="danger"
          onConfirm={confirm.onConfirm}
          onCancel={() => setConfirm(null)}
        />
      )}
    </div>
  );
}
